import { useEffect, useMemo } from 'react'

// Provides a low-res grayscale frame from either the webcam or a fallback image.
// Returns a stable object; call `.update()` each frame to pull the latest pixels
// into `.gray` (Uint8 0-255, length W*H). No model, no ML — just pixels.
export function useCVSource({ W, H, useWebcam, fallbackSrc }) {
  const src = useMemo(() => {
    const canvas = document.createElement('canvas')
    canvas.width = W
    canvas.height = H
    return {
      W, H,
      gray: new Uint8ClampedArray(W * H),
      ready: false,
      mode: 'image',     // 'image' | 'webcam'
      _canvas: canvas,
      _ctx: canvas.getContext('2d', { willReadFrequently: true }),
      _video: null,
      _img: null,
      _stream: null,
      update() {
        const source =
          this.mode === 'webcam' && this._video && this._video.readyState >= 2
            ? this._video
            : this._img
        if (!source) return
        // cover-fit the source into W×H (mirror webcam so it feels like a mirror)
        const ctx = this._ctx
        ctx.save()
        if (this.mode === 'webcam' && source === this._video) {
          ctx.translate(W, 0)
          ctx.scale(-1, 1)
        }
        ctx.drawImage(source, 0, 0, W, H)
        ctx.restore()
        const d = ctx.getImageData(0, 0, W, H).data
        const g = this.gray
        for (let i = 0; i < W * H; i++) {
          g[i] = d[i * 4] * 0.299 + d[i * 4 + 1] * 0.587 + d[i * 4 + 2] * 0.114
        }
        this.ready = true
      },
    }
  }, [W, H])

  // Load the fallback image once
  useEffect(() => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => { src._img = img }
    img.src = fallbackSrc
  }, [src, fallbackSrc])

  // Start/stop the webcam when toggled
  useEffect(() => {
    let cancelled = false
    if (!useWebcam) {
      src.mode = 'image'
      return
    }
    navigator.mediaDevices
      ?.getUserMedia({ video: { width: 320, height: 240 }, audio: false })
      .then(stream => {
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return }
        const video = document.createElement('video')
        video.srcObject = stream
        video.muted = true
        video.playsInline = true
        video.play()
        src._video = video
        src._stream = stream
        src.mode = 'webcam'
      })
      .catch(() => { src.mode = 'image' })  // denied / unavailable → stay on image

    return () => {
      cancelled = true
      if (src._stream) {
        src._stream.getTracks().forEach(t => t.stop())
        src._stream = null
        src._video = null
      }
      src.mode = 'image'
    }
  }, [useWebcam, src])

  return src
}
